import {useQuery} from '@tanstack/react-query'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {BR_SEARCH_URL} from '../constants'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {formatDate} from '../utils/product-utils'
import {getOrCreateBrUid2} from '../utils/common-utils'

const useBloomreach = (query, enabled, phrase, isCategory = false, effectiveDateTime) => {
    const suggestApiUrl = '/api/v2/suggest/'
    const {
        app: {commerceAPI}
    } = getConfig()
    const appOrigin = getAppOrigin()
    const {bloomreach} = commerceAPI
    const {config} = bloomreach || {}

    const {storefront} = commerceAPI
    const {caching, hostUrl} = storefront || {}
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })

    var enableBloomreachSuggestions = false,
        catalogViews = '',
        accountId = '',
        suggestBaseUrl = 'suggest.dxpapi.com',
        suggestionsCacheTime = 2 * 60 * 60 * 1000
    try {
        enableBloomreachSuggestions = JSON.parse(config).enableBloomreachSuggestions
        accountId = JSON.parse(config).accountId
        suggestBaseUrl = JSON.parse(config).suggestBaseUrl
        catalogViews = JSON.parse(config).catalogViews
    } catch (e) {
        console.log(e.message)
    }

    try {
        const cachingConfig = JSON.parse(caching)
        suggestionsCacheTime = cachingConfig.suggestions
    } catch (e) {
        console.log(e.message)
    }
    const getBRQuerySuggestions = async (query) => {
        const bloomreachAPIUrl = `https://${suggestBaseUrl}${suggestApiUrl}`
        const brUid = getOrCreateBrUid2(cookie)
        const apiUrl = `${bloomreachAPIUrl}?q=${query}&account_id=${accountId}&catalog_views=${catalogViews}&request_type=suggest&_br_uid_2=${brUid}`
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                accept: 'application/json'
            },
            credentials: 'omit'
        })
        return await response.json()
    }

    const getBRPhraseSuggestions = async (query) => {
        const bloomreachAPIUrl = `https://${suggestBaseUrl}${suggestApiUrl}`
        const brUid = getOrCreateBrUid2(cookie)
        const apiUrl = `${bloomreachAPIUrl}?q=${query}&account_id=${accountId}&catalog_views=${catalogViews}&request_type=suggest&_br_uid_2=${brUid}`
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                accept: 'application/json'
            },
            credentials: 'omit'
        })
        const awaitResponse = await response.json()
        return awaitResponse?.suggestionGroups?.[0]?.searchSuggestions
    }

    const getBRCategoryProducts = async (query) => {
        const bloomreachAPIUrl = `${appOrigin}${BR_SEARCH_URL}`
        const formattedDate = formatDate(effectiveDateTime)
        const currentDate = formatDate()
        const disableRules = formattedDate > currentDate

        const baseParams = new URLSearchParams({
            fl: 'pid,sale_price,thumb_image,title,variant_url',
            search_type: 'category',
            start: '0',
            rows: '7',
            account_id: accountId,
            domain_key: catalogViews,
            q: query,
            request_type: 'search',
            sort: '',
            ref_url: `${hostUrl}${location?.pathname}${location?.search}`,
            url: `${hostUrl}${location?.pathname}${location?.search}`
        })

        // if the cookie comes in encoded, decode it as .append might re-encode it
        let brUid = getOrCreateBrUid2(cookie)
        try {
            if (brUid.includes('%')) {
                brUid = decodeURIComponent(brUid)
            }
        } catch (e) {
            // keep brUid as-is if decode fails
        }

        baseParams.append('_br_uid_2', brUid)

        if (disableRules) {
            baseParams.append('merchandising_rules.disable', 'attribute_include,attribute_exclude')
        }

        const apiUrl = `${bloomreachAPIUrl}?${baseParams.toString()}`

        try {
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {accept: 'application/json', formatteddate: formattedDate},
                credentials: 'omit'
            })

            if (!response.ok) {
                console.error(`Bloomreach API error: ${response.status}`)
                return []
            }

            const data = await response.json()
            return data?.response?.docs || []
        } catch (error) {
            console.error('Failed to fetch Bloomreach category products:', error.message)
            return []
        }
    }

    return useQuery({
        queryKey: ['use-bloomreach', query + (phrase || '')],
        queryFn: () =>
            isCategory
                ? getBRCategoryProducts(query)
                : phrase
                ? getBRPhraseSuggestions(query)
                : getBRQuerySuggestions(query),
        enabled: !!enabled && !!query && !!enableBloomreachSuggestions,
        staleTime: suggestionsCacheTime,
        cacheTime: suggestionsCacheTime,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        keepPreviousData: true
    })
}

export default useBloomreach
